/**
 * InstitutionAdminDashboard — per-institution admin landing page.
 *
 * Scope (Phase 1):
 *   • Invite users (teacher / student / institution_admin)
 *   • See active modules (read-only — content is authored by `developer` role)
 *   • Quick links: Chat Analytics (if granted) + Preview as Student
 *
 * Future phases will add: LLM spend cap, suspend/restore courses,
 * institution-wide settings, branding self-edit.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ModulePill from '../components/ModulePill';
import { getModules, createInvite } from '../lib/apiClient';
import { useBrand } from '../context/BrandContext';

const InstitutionAdminDashboard = () => {
  const brand = useBrand();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite state
  const [inviteRole, setInviteRole] = useState('teacher');
  const [inviteEmail, setInviteEmail] = useState('');
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const inviteInputRef = useRef(null);

  useEffect(() => {
    getModules().then(setModules).finally(() => setLoading(false));
  }, []);

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setGeneratedInvite(null);
    setCreatingInvite(true);
    try {
      const data = await createInvite(inviteRole, inviteEmail);
      setGeneratedInvite(data);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const inviteDisplayUrl = generatedInvite
    ? generatedInvite.invite_url.replace(/https?:\/\/[^/]+/, window.location.origin)
    : '';

  const copyInviteLink = async () => {
    if (!inviteDisplayUrl) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(inviteDisplayUrl);
        setCopyStatus('Copied!');
        setTimeout(() => setCopyStatus(''), 2000);
        return;
      }
    } catch (_) { /* fall through */ }
    try {
      inviteInputRef.current?.focus();
      inviteInputRef.current?.select();
      if (document.execCommand('copy')) {
        setCopyStatus('Copied!');
        setTimeout(() => setCopyStatus(''), 2000);
        return;
      }
    } catch (_) { /* fall through */ }
    setCopyStatus('Press Ctrl/Cmd+C to copy');
    setTimeout(() => setCopyStatus(''), 3000);
  };

  return (
    <div className="min-h-screen" data-testid="institution-admin-dashboard">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Institution Admin</h1>
            <p className="text-gray-500 mt-1">
              Managing <span className="font-semibold">{brand.institution_name}</span> on {brand.platform_name}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/rubrics"
              className="btn-outline text-sm"
              data-testid="ia-rubrics-link"
            >
              📝 Rubrics
            </Link>
            <Link
              to="/preview-as-student"
              className="btn-outline text-sm"
              data-testid="ia-preview-as-student-btn"
            >
              👁 Preview as Student
            </Link>
          </div>
        </div>

        {/* Invite section */}
        <section className="card p-6 mb-10" data-testid="ia-invite-section">
          <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--unthsc-blue)' }}>
            Invite Faculty or Students
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Generate a one-time link. Copy and email it to the recipient.
          </p>
          <form onSubmit={handleCreateInvite} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="input-field"
                data-testid="ia-invite-role-select"
              >
                <option value="student">Student</option>
                <option value="teacher">Teacher (Faculty / TA)</option>
                <option value="institution_admin">Institution Admin (co-admin)</option>
              </select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email (optional)</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                className="input-field"
                data-testid="ia-invite-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={creatingInvite}
              className="btn-primary"
              data-testid="ia-invite-create-btn"
            >
              {creatingInvite ? 'Creating…' : 'Generate Link'}
            </button>
          </form>
          {inviteError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="ia-invite-error">
              {inviteError}
            </div>
          )}
          {generatedInvite && (
            <div className="mt-4 p-4 rounded-lg" style={{ background: 'var(--unthsc-green-light, #ecfdf5)' }} data-testid="ia-invite-result">
              <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--unthsc-green-dark, #047857)' }}>
                Invite for {generatedInvite.role}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  ref={inviteInputRef}
                  readOnly
                  value={inviteDisplayUrl}
                  onFocus={(e) => e.target.select()}
                  className="input-field flex-1"
                />
                <button onClick={copyInviteLink} className="btn-secondary text-sm" data-testid="ia-invite-copy-btn">
                  {copyStatus || 'Copy'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Modules (read-only) */}
        <section data-testid="ia-modules-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Modules</h2>
          <p className="text-sm text-gray-500 mb-4">
            Content authoring is handled by the developer role. Contact your platform operator to add or edit modules.
          </p>
          {loading ? (
            <div className="text-gray-500">Loading…</div>
          ) : modules.length === 0 ? (
            <div className="card p-8 text-center text-gray-500" data-testid="ia-no-modules">
              No modules yet — your developer can create them.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ia-modules-list">
              {modules.map((m) => (
                <div key={m.id} className="card p-4" data-testid={`ia-module-${m.order}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="mb-2"><ModulePill number={m.order} compact /></div>
                      <h3 className="font-bold text-gray-900 truncate">{m.title}</h3>
                      <div className="text-xs text-gray-500 mt-1">
                        {(m.topics || []).length} topic{(m.topics || []).length === 1 ? '' : 's'}
                      </div>
                    </div>
                    <span className={m.status === 'published' ? 'badge-published' : 'badge-draft'}>
                      {m.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default InstitutionAdminDashboard;
